import http, { BASE_URL_3 } from '../../http';
import type { CourseDetail } from '../../../models/course-detail.entity';
import type { CourseGroup } from '../../../models/course-group.entity';
import type { FirstMenu } from '../../../models/first-menu.entity';
import type { MenuDish } from '../../../models/menu-dish.entity';
import type { SecondMenu } from '../../../models/second-menu.entity';

/**
 * 解析请求用的餐厅码：必须显式传入菜单库模块内餐厅码，禁止静默回退全局当前店。
 * @param rbpStringId - 餐厅码
 * @throws 未传或为空时抛错，避免错店调用
 */
function resolveCloudDishLibraryStringId(rbpStringId?: string): string {
    const sid = (rbpStringId && rbpStringId.trim()) || '';
    if (!sid) {
        throw new Error('cloud-dish-library: rbpStringId (restaurant string_id) is required');
    }
    return sid;
}

/** 上传本地菜单到云端 — 请求体 */
export interface CloudDishLibraryUploadBody {
    /** 本地一级菜单 id 列表 */
    main_group_ids: number[];
}

/**
 * 上传成功响应中的云端菜单摘要（联调真实体）
 * 例：`{ id, main_group_name, dish_count, menu_image, source_main_group_id }`
 */
export interface CloudDishLibraryUploadedMenu {
    /** 新建云端菜单 id */
    id: string;
    /** 菜单名称 */
    main_group_name: string;
    /** 该份菜品数 */
    dish_count: number;
    /** 封面 URL */
    menu_image: string;
    /** 来源本地 `main_group_id` */
    source_main_group_id: number;
}

/**
 * 上传本地菜单到云端 — 成功响应（联调真实体）
 * 例：`{ uploaded_count, total_dish_count, menus }`
 */
export interface CloudDishLibraryUploadResult {
    /** 本次新建的云端菜单份数 */
    uploaded_count: number;
    /** 本次涉及菜品总数 */
    total_dish_count: number;
    /** 新建的云端菜单摘要列表 */
    menus: CloudDishLibraryUploadedMenu[];
}

/**
 * 云端菜单列表项内嵌的小类摘要（`GET .../menus` 的 `menu_item_slu[]`；无菜品树，供复制/转移选目标二级菜单）
 */
export interface CloudDishLibraryMenuListSluItem {
    /** 云端小类 id（复制/转移 body 的 `target_cloud_slu_id`） */
    _id: string;
    /** 本地同源小类数字 id；云端手工创建时常为 `0`，不可作选中键 */
    dmi_slu_id: number;
    /** 小类名称 */
    dmi_slu_name: string;
    /** 小类下标 */
    dmi_slu_index: number;
    /** 是否不参与全局限点 */
    not_joining_global_limit: number;
    /** 分类点餐限点 */
    category_order_limit: number;
    /** 桌台分类点餐限点 */
    table_category_order_limit: number;
    /** 小类多语言 JSON 字符串 */
    dmi_slu_name_i18n: string;
    /** 该小类下菜品数 */
    dish_count: number;
}

/**
 * 云端菜单列表项（联调真实体，`GET .../menus` 数组元素）
 * 例：`{ id, main_group_name, dish_count, second_group_count, menu_image, hasImage, source_main_group_id, created_at, created_by, menu_item_slu }`
 */
export interface CloudDishLibraryMenuListItem {
    /** 云端菜单 id */
    id: string;
    /** 菜单名称 */
    main_group_name: string;
    /** 菜品数 */
    dish_count: number;
    /** 小类（二级菜单）数 */
    second_group_count: number;
    /** 封面 URL */
    menu_image: string;
    /** 是否有封面图 */
    hasImage: boolean;
    /** 来源本地 `main_group_id` */
    source_main_group_id: number;
    /** 创建时间（ISO 8601） */
    created_at: string;
    /** 创建人 staff id */
    created_by: string;
    /**
     * 小类摘要列表（无 `menu_item`）；用于云端菜品复制/转移时跨菜单选择目标二级菜单。
     * 旧响应可能缺省；规范化后前端按空数组处理。
     */
    menu_item_slu?: CloudDishLibraryMenuListSluItem[];
}

/** 批量删除云端菜品 — 请求体 */
export interface CloudDishLibraryDishesDeleteBody {
    /** 待删除的云端菜品 id */
    cloud_dish_ids: string[];
}

/**
 * 批量删除云端菜品 — 成功响应（OAS example）
 * 例：`{ deleted_dish_count, deleted_oss_count, not_found_ids, dish_count }`
 */
export interface CloudDishLibraryDishesDeleteResult {
    /** 实际删除的菜品数 */
    deleted_dish_count: number;
    /** 清理的 OSS 资源数 */
    deleted_oss_count: number;
    /** 请求中未找到、未删除的云端菜品 id */
    not_found_ids: string[];
    /** 删除后当前云端菜单剩余菜品数 */
    dish_count: number;
}

/** 转移云端菜品 — 请求体 */
export interface CloudDishLibraryDishesMoveBody {
    /** 待转移的云端菜品 id */
    cloud_dish_ids: string[];
    /** 目标云端小类 id（前端转移必传） */
    target_cloud_slu_id?: string;
    /** 目标云端菜单 id（可跨菜单；前端转移必传） */
    target_cloud_menu_id?: string;
}

/**
 * 转移云端菜品 — 成功响应（OAS example）
 * 例：`{ moved_count, moved_ids, skipped_same_target_count, not_found_ids, source_dish_count, target_dish_count }`
 */
export interface CloudDishLibraryDishesMoveResult {
    /** 实际转移成功的菜品数 */
    moved_count: number;
    /** 已转移的云端菜品 id */
    moved_ids: string[];
    /** 因目标与源相同而跳过的数量 */
    skipped_same_target_count: number;
    /** 未找到的云端菜品 id */
    not_found_ids: string[];
    /** 转移后源菜单（或源小类语境）菜品数 */
    source_dish_count: number;
    /** 转移后目标侧菜品数 */
    target_dish_count: number;
}

/** 复制云端菜品 — 请求体（三项均必填） */
export interface CloudDishLibraryDishesCopyBody {
    /** 待复制的云端菜品 id */
    cloud_dish_ids: string[];
    /** 目标云端菜单 id */
    target_cloud_menu_id: string;
    /** 目标云端小类 id */
    target_cloud_slu_id: string;
}

/** 复制成功后的源→新菜品 id 映射项 */
export interface CloudDishLibraryCopiedDishRef {
    /** 源云端菜品 id */
    source_id: string;
    /** 新建云端菜品 id */
    id: string;
}

/**
 * 复制云端菜品 — 成功响应（OAS example）
 * 例：`{ copied_count, copied_dishes, not_found_ids, target_dish_count }`
 */
export interface CloudDishLibraryDishesCopyResult {
    /** 实际复制成功的菜品数 */
    copied_count: number;
    /** 源 id → 新 id 映射列表 */
    copied_dishes: CloudDishLibraryCopiedDishRef[];
    /** 未找到的云端菜品 id */
    not_found_ids: string[];
    /** 复制后目标小类/菜单侧菜品数 */
    target_dish_count: number;
}

/** 删除云端菜单 — 请求体 */
export interface CloudDishLibraryDeleteBody {
    /** 待删除的云端菜单 id */
    cloud_menu_ids: string[];
}

/**
 * 组装单个云端菜单删除请求体。
 * @param cloudMenuId - 待删除的云端菜单 id
 * @returns 云端菜单删除接口请求体
 */
export function buildCloudDishLibrarySingleMenuDeleteBody(cloudMenuId: string): CloudDishLibraryDeleteBody {
    const id = String(cloudMenuId ?? '').trim();
    if (!id) {
        throw new Error('cloud_menu_id is required');
    }
    return { cloud_menu_ids: [id] };
}

/**
 * 组装云端小类删除请求体。
 * @param cloudSluId - 待删除的云端小类 id
 * @returns 云端菜单 update 接口的小类删除字段
 */
export function buildCloudDishLibrarySubcategoryDeleteBody(cloudSluId: string): CloudDishLibraryUpdateMenuBody {
    const id = String(cloudSluId ?? '').trim();
    if (!id) {
        throw new Error('cloud_slu_id is required');
    }
    return { menu_item_slu_delete: [id] };
}

/**
 * 删除云端菜单 — 成功响应（联调真实体）
 * 例：`{ deleted_menu_count, deleted_dish_count, deleted_oss_count, not_found_ids }`
 */
export interface CloudDishLibraryDeleteResult {
    /** 实际删除的云端菜单份数 */
    deleted_menu_count: number;
    /** 实际删除涉及的菜品数 */
    deleted_dish_count: number;
    /** 清理的 OSS 资源数 */
    deleted_oss_count: number;
    /** 请求中未找到、未删除的云端菜单 id */
    not_found_ids: string[];
}

/** 云端菜品添加到本地 — 请求体 */
export interface CloudDishLibraryAddDishesBody {
    /** 云端一级菜单 id */
    cloud_menu_id: string;
    /** 选中的云端菜品 id */
    cloud_dish_ids: string[];
    /** 目标本地大类 id */
    target_main_group_id: number;
    /** 目标本地小类 id */
    target_dmi_slu_id: number;
}

/**
 * add-dishes 顺带创建的套餐菜品明细（拉取套餐时非空；联调真实体）。
 */
export interface CloudDishLibraryCreatedCourseDish {
    /** 所属套餐名称（对应 added_names 中的套餐） */
    set_meal_name: string;
    /** 套餐内分组名（如必选组 / 可选组） */
    course_group_name: string;
    /** 新建菜品名称 1（成功弹窗展示用） */
    item_name1: string;
    /** 新建菜品名称 2（接口带回；弹窗本期不展示） */
    item_name2: string;
}

/**
 * 云端菜品添加到本地 — 成功响应（联调真实体）
 * 例：`{ added_count, added_names, skipped_duplicate_count, skipped_duplicate_names, created_course_dish_count, created_course_dishes, created_condiment_count, created_condiments }`
 *
 * `created_condiments` 元素结构待非空样例再收紧。
 */
export interface CloudDishLibraryAddDishesResult {
    /** 成功添加到本地的菜品数 */
    added_count: number;
    /** 成功添加的菜品名称列表 */
    added_names: string[];
    /** 因本地已存在而跳过的菜品数 */
    skipped_duplicate_count: number;
    /** 跳过的重复菜品名称列表 */
    skipped_duplicate_names: string[];
    /** 顺带创建的套餐菜品数 */
    created_course_dish_count: number;
    /** 顺带创建的套餐菜品明细 */
    created_course_dishes: CloudDishLibraryCreatedCourseDish[];
    /** 顺带创建的配料数 */
    created_condiment_count: number;
    /** 顺带创建的配料明细（元素结构待非空样例收紧） */
    created_condiments: unknown[];
}

/**
 * 云端套餐组内明细（`dishes/create` / `dishes/update` 的 `course_groups[].course_detail[]`）。
 * `source_menu_item_id` ← 选中菜 `item_id`；`dish_origin_id` ← 选中菜 `origin_id`。
 */
export interface CloudDishLibraryCourseDetail {
    /** 来源菜品 item_id */
    source_menu_item_id: number;
    /** 套餐内展示名 */
    item_course_name: string;
    /** 规格单位（套餐明细） */
    unit: string;
    /** 份数 */
    num: number;
    /** 套餐内加价 */
    price: number;
    /** 套餐明细备注 */
    description: string;
    /** 单人限点数量 */
    rule_lnr_max_num: number;
    /** 来源菜品 origin_id */
    dish_origin_id: string;
    /** 名称1 */
    item_name1: string;
    /** 名称2 */
    item_name2: string;
    /** 菜品类型 */
    item_type: number;
    /** 折扣分项器 class_id */
    class_id: number;
    /** 税率组 */
    tax_group: number;
    /** 报表主类 */
    major_group: number;
    /** 报表子类 */
    family_group: number;
    /** 堂食价 */
    price_1: number;
    /** 过敏原 */
    allergen: string[];
    /** 菜品描述 */
    item_description: string;
    /** 外卖价 */
    takeout_price_1: number;
    /** 规格1 */
    unit_1: string;
    /** 助记码 */
    nlu: string | null;
    /** 额外备注 */
    ext_dish_info: string;
    /** 其它明细标签 */
    other_detail_info: string[];
    /** 是否不参与全局限点 */
    not_joining_global_limit: number;
    /** 岗位打印组 */
    print_class: number;
    /** 来源类型（如 SW2） */
    origin_type: string;
    /** 来源菜品单人限点上限 */
    dish_rule_lnr_max_num: number;
    /** 限点开关 */
    rule_lnr_enable: number;
    /** 超限后仍可点 */
    rule_lnr_limit_enable: number;
    /** 超限后价格 */
    rule_lnr_limit_price: number;
    /** 限点是否与人数挂钩 */
    rule_lnr_max_num_with_customer: number;
    /** 桌台限点 */
    rule_table_max_num: number;
}

/**
 * 云端套餐组（`dishes/create` / `dishes/update` 的 `course_groups[]` 元素）。
 */
export interface CloudDishLibraryCourseGroup {
    /** 套餐组名称 */
    course_group_name: string;
    /** 是否必选：1 必选 / 0 可选 */
    is_must: number;
    /** 必选/可选份数 */
    choose_num: number;
    /** 关联小类 id；无则 -1 */
    slu_id: number;
    /** 来源类型 */
    origin_type: string;
    /** 来源 id */
    origin_id: string;
    /** 来源菜品类型 */
    origin_dish_type: string;
    /** 组排序权重 */
    slu_priority: number;
    /** 是否必选满 */
    is_full_select: number;
    /** 组内菜品明细 */
    course_detail: CloudDishLibraryCourseDetail[];
}

/**
 * 云端详情菜品行：在本地 `MenuDish` 基础上扩展云端 id。
 * `id` 为云端菜品 string id（覆盖本地特色菜用的 number `id`）。
 */
export interface CloudDishLibraryMenuDetailDish extends Omit<MenuDish, 'id'> {
    /** 云端菜品 id */
    id: string;
    /** 所属云端菜单 id */
    cloud_menu_id: string;
    /** 所属云端小类 id */
    cloud_slu_id: string;
    /** 小类名称（详情菜品行可能带回） */
    dmi_slu_name?: string;
    /** 小类下标（详情菜品行可能带回） */
    dmi_slu_index?: number;
    /** 套餐组（详情回填；非套餐可省略） */
    course_groups?: CloudDishLibraryCourseGroup[];
}

/**
 * 云端详情小类：在本地 `SecondMenu` 基础上扩展云端 `_id` 与计数。
 */
export interface CloudDishLibraryMenuDetailSlu extends Omit<SecondMenu, 'menu_item'> {
    /** 云端小类 id */
    _id: string;
    /** 该小类下菜品数 */
    dish_count?: number;
    /** 云端菜品列表（本地字段 + 云端 id） */
    menu_item?: CloudDishLibraryMenuDetailDish[];
}

/**
 * 云端菜单详情（联调真实体）：在本地 `FirstMenu`（`get_menus_v3` 单棵）基础上扩展。
 * - 云端主键：`id`（string）；来源本地：`source_main_group_id`
 * - 另含 `dish_count` / `second_group_count` / `hasImage` / `created_at` / `created_by` 等
 * - `menu_item_slu` 为云端扩展小类树
 */
export interface CloudDishLibraryMenuDetail extends Omit<FirstMenu, 'menu_item_slu'> {
    /** 云端菜单 id */
    id: string;
    /** 来源本地一级菜单 `main_group_id` */
    source_main_group_id: number;
    /** 菜品总数 */
    dish_count: number;
    /** 小类总数 */
    second_group_count: number;
    /** 是否有封面图 */
    hasImage?: boolean;
    /** 本地同款：菜单内菜品最大 index（若后端带回） */
    max_index?: number;
    /**
     * 菜单级「菜品不受轮次限制」：`1`=不受限，`0`=受限；详情顶栏开关优先读此字段。
     */
    not_joining_global_limit?: number;
    /** 创建时间（ISO 8601） */
    created_at?: string;
    /** 创建人 staff id */
    created_by?: string;
    /** 云端小类 + 菜品树 */
    menu_item_slu?: CloudDishLibraryMenuDetailSlu[];
}

/** 云端菜单创建 — 小类项（可随头区一并提交；本前端创建流程先空数组再 update 追加） */
export interface CloudDishLibraryCreateMenuSluItem {
    /** 小类名称 */
    dmi_slu_name: string;
    /** 小类多语言 JSON 字符串 */
    dmi_slu_name_i18n?: string;
    /** 每人限点 */
    category_order_limit?: number;
    /** 桌台限点 */
    table_category_order_limit?: number;
}

/** 云端菜单创建 — 请求体（与本地一级创建字段对齐，另强制 `exact_search_rules` / `menu_item_slu`） */
export interface CloudDishLibraryCreateMenuBody {
    /** 菜单名称 */
    main_group_name: string;
    /** 多语言 JSON 字符串 */
    main_group_name_i18n: string;
    /** 排序下标 */
    main_group_index: number;
    /** 精确搜索规则（OAS 必填；默认 0） */
    exact_search_rules: number;
    /** 大类限点 */
    max_limit: number;
    /** 价格方案：`'0'` / `'1'` */
    set_item_main: string;
    /**
     * 菜单级「菜品不受轮次限制」：`1`=不受限，`0`=受限。
     * 详情顶栏开关优先读 GET 同名字段；切换仍走 `batch-not-limit`。
     */
    not_joining_global_limit: number;
    /** 初始小类列表（分步创建时传 `[]`） */
    menu_item_slu: CloudDishLibraryCreateMenuSluItem[];
}

/**
 * 云端菜单创建 — 成功响应（联调真实体；在列表项基础上多 `create_source` / `updated_at`）
 * 例：`{ id, main_group_name, dish_count, second_group_count, menu_image, hasImage, source_main_group_id, create_source, created_at, updated_at, created_by }`
 */
export interface CloudDishLibraryCreateMenuResult extends CloudDishLibraryMenuListItem {
    /** 创建来源（联调样例为 `manual`） */
    create_source: string;
    /** 最近更新时间（ISO 8601） */
    updated_at: string;
}

/** 云端菜单更新 — 追加小类 */
export interface CloudDishLibraryUpdateMenuSluAddItem {
    /** 小类名称 */
    dmi_slu_name: string;
    /** 每人限点 */
    category_order_limit?: number;
    /** 桌台限点 */
    table_category_order_limit?: number;
    /** 小类多语言 JSON */
    dmi_slu_name_i18n?: string;
}

/** 云端菜单更新 — 修改已有小类（`_id` 为详情小类 id） */
export interface CloudDishLibraryUpdateMenuSluUpdateItem {
    /** 云端小类 `_id` */
    _id: string;
    /** 小类名称 */
    dmi_slu_name?: string;
    /** 每人限点 */
    category_order_limit?: number;
    /** 桌台限点 */
    table_category_order_limit?: number;
    /** 小类多语言 JSON */
    dmi_slu_name_i18n?: string;
    /** 小类排序下标（OAS 示例 6：`menu_item_slu_update[].dmi_slu_index`，从 1 连续） */
    dmi_slu_index?: number;
}

/**
 * 从当前小类列表生成云端小类排序更新体（`POST .../menus/{cloud_menu_id}/update` 的 `menu_item_slu_update`）。
 * @param slus - 详情左侧小类列表（须含云端 `_id`）
 * @returns 每项含 `_id` + `dmi_slu_index`；无 `_id` 的行跳过
 */
export function buildCloudMenuItemSluIndexUpdateBody(
    slus: SecondMenu[] | null | undefined,
): CloudDishLibraryUpdateMenuSluUpdateItem[] {
    if (!slus?.length) {
        return [];
    }
    const items: CloudDishLibraryUpdateMenuSluUpdateItem[] = [];
    slus.forEach((slu, index) => {
        const rawId = (slu as { _id?: string })._id;
        const cloudSluId = typeof rawId === 'string' ? rawId.trim() : '';
        if (!cloudSluId) {
            return;
        }
        items.push({
            _id: cloudSluId,
            dmi_slu_index: index + 1,
        });
    });
    return items;
}

/** 云端菜品排序 — body 中单条（`cloud_dish_id` + `slu_priority`） */
export interface CloudDishLibraryDishesSortItem {
    /** 云端菜品 string id */
    cloud_dish_id: string;
    /**
     * 同小类内展示权重：与本地 / RBP2 一致，首行最大（`length - index`），配合 `slu_priority DESC`
     */
    slu_priority: number;
}

/**
 * 云端菜品排序 — 请求体
 * `POST .../menus/{cloud_menu_id}/dishes/sort`
 */
export interface CloudDishLibraryDishesSortBody {
    /** 目标云端小类 `_id` */
    cloud_slu_id: string;
    /** 该小类下菜品新顺序 */
    dishes: CloudDishLibraryDishesSortItem[];
}

/** 云端菜品排序 — 200（OAS schema 为空，联调后可收紧） */
export type CloudDishLibraryDishesSortResult = unknown;

/**
 * 从当前小类菜品列表生成云端菜品排序 body。
 * 与本地 `buildDetailDishSluPriorityIndexPayload` / RBP2 一致：`slu_priority = length - index`（首行最大）。
 * @param cloudSluId - 云端小类 `_id`
 * @param items - 当前小类 `menu_item`（按展示顺序）
 * @returns 含 `cloud_slu_id` + `dishes`；小类 id 空或无可排序菜品时返回 null
 */
export function buildCloudDishLibraryDishesSortBody(
    cloudSluId: string,
    items: MenuDish[] | null | undefined,
): CloudDishLibraryDishesSortBody | null {
    const sluId = typeof cloudSluId === 'string' ? cloudSluId.trim() : '';
    if (!sluId || !items?.length) {
        return null;
    }
    /** 展示列表长度，用于 `length - index` */
    const length = items.length;
    const dishes: CloudDishLibraryDishesSortItem[] = [];
    for (let index = 0; index < length; index += 1) {
        const dish = items[index];
        const rawId = (dish as { id?: string | number }).id;
        const cloudDishId =
            rawId != null && String(rawId).trim() !== '' ? String(rawId).trim() : '';
        if (!cloudDishId) {
            continue;
        }
        dishes.push({
            cloud_dish_id: cloudDishId,
            slu_priority: length - index,
        });
    }
    if (!dishes.length) {
        return null;
    }
    return {
        cloud_slu_id: sluId,
        dishes,
    };
}

/**
 * 云端菜单更新 — 请求体（局部提交；与 OAS 示例 2–5 一致，可只带变更字段）
 */
export interface CloudDishLibraryUpdateMenuBody {
    /** 菜单名称 */
    main_group_name?: string;
    /** 多语言 JSON */
    main_group_name_i18n?: string;
    /** 大类限点 */
    max_limit?: number;
    /** 追加小类 */
    menu_item_slu_add?: CloudDishLibraryUpdateMenuSluAddItem[];
    /** 更新已有小类 */
    menu_item_slu_update?: CloudDishLibraryUpdateMenuSluUpdateItem[];
    /** 删除小类 id 列表（内有菜品时后端拒绝） */
    menu_item_slu_delete?: string[];
}

/**
 * 云端菜单更新响应中的小类摘要（`added_slu` / `updated_slu` 元素；联调真实体）
 */
export interface CloudDishLibraryUpdateMenuSluResultItem {
    /** 云端小类 `_id`（新增后可作为 `cloud_slu_id`） */
    _id: string;
    /** 小类名称 */
    dmi_slu_name: string;
    /** 每人限点 */
    category_order_limit?: number;
    /** 桌台限点 */
    table_category_order_limit?: number;
    /** 小类多语言 JSON 字符串 */
    dmi_slu_name_i18n?: string;
}

/**
 * 云端菜单更新 — 成功响应（联调真实体）
 * 在 create 结果上多 `max_limit` 与小类变更摘要 `added_slu` / `updated_slu` / `deleted_slu`。
 */
export interface CloudDishLibraryUpdateMenuResult extends CloudDishLibraryCreateMenuResult {
    /** 大类限点 */
    max_limit: number;
    /** 本次新增的小类列表（`menu_item_slu_add` 时非空） */
    added_slu: CloudDishLibraryUpdateMenuSluResultItem[];
    /** 本次更新的小类列表（结构与 `added_slu` 同形） */
    updated_slu: CloudDishLibraryUpdateMenuSluResultItem[];
    /**
     * 本次删除的小类 id 列表（暂无非空联调样例；前端删除路径不依赖此字段形态）
     */
    deleted_slu: string[];
}

/**
 * 上传/覆盖云端菜单图 — 成功响应（联调真实体）
 * 例：`{ id, menu_image, hasImage, updated_at }`
 */
export interface CloudDishLibraryMenuImageResult {
    /** 云端菜单 id */
    id: string;
    /** 封面完整 URL（OSS） */
    menu_image: string;
    /** 是否已有封面图 */
    hasImage: boolean;
    /** 最近更新时间（ISO 8601） */
    updated_at: string;
}

/**
 * 云端菜品新增与本地 `MenuDish` 对齐的字段键（不含本地 slu / item 主键）。
 * 故意不复用 `PatchDishesParams`：后者把 `allergen` / `other_detail_info` 收成序列化 string。
 */
type CloudDishLibraryCreateDishMenuDishKeys =
    | 'item_name1'
    | 'item_name2'
    | 'item_type'
    | 'nlu'
    | 'tax_group'
    | 'print_class'
    | 'required_condiment'
    | 'class_id'
    | 'major_group'
    | 'family_group'
    | 'special_attributes'
    | 'price_1'
    | 'takeout_price_1'
    | 'rule_lnr_max_num'
    | 'rule_table_max_num'
    | 'rule_lnr_limit_price'
    | 'minimum_time'
    | 'ext_dish_info'
    | 'item_i18n'
    | 'allergen'
    | 'other_detail_info'
    | 'rules_ignore_the_display'
    | 'rule_lnr_limit_enable'
    | 'not_joining_global_limit';

/** 云端新增菜品 — 调味品组内单项（不含 item_id） */
export interface CloudDishLibraryCreateDishCondimentItem {
    /** 名称1 */
    item_name1: string;
    /** 名称2 */
    item_name2: string;
    /** 助记码 */
    nlu: string | null;
    /** 税率组 id */
    tax_group: number;
    /** 自定义报表主类别 */
    major_group: number;
    /** 自定义报表子类别 */
    family_group: number;
    /** 堂食价 */
    price_1: number;
    /** 过敏原列表 */
    allergen: string[];
    /** 描述 */
    item_description: string;
    /** 其它明细（配料等） */
    other_detail_info: string;
    /** 外卖价 */
    takeout_price_1: number;
    /** 规格单位 */
    unit_1: string;
}

/** 云端新增菜品 — 调味品组快照 */
export interface CloudDishLibraryCreateDishCondimentGroup {
    /** 来源调味品组 id（与本地 scomdiment_groups_id 相同） */
    source_condiment_groups_id: number;
    /** 调味品组 id */
    scomdiment_groups_id: number;
    /** 调味品组名称 */
    comdiment_groups_name: string;
    /** 触屏样式；无则 null */
    touchscreen_style: unknown | null;
    /** 组价格；缺省 0 */
    price: number;
    /** 组内调味品列表 */
    comdiments: CloudDishLibraryCreateDishCondimentItem[];
}

/** 映射输入：本地 get_condiments_group 单项（允许多余字段） */
export type CloudDishCondimentGroupSource = {
    /** 调味品组 id */
    scomdiment_groups_id?: number;
    /** 调味品组名称 */
    comdiment_groups_name?: string;
    /** 触屏样式 */
    touchscreen_style?: unknown | null;
    /** 组价格 */
    price?: number;
    /** 组内调味品 */
    comdiments?: Array<Record<string, unknown>>;
};

/**
 * 将本地过敏原表示转为 string[]。
 * @param raw - 数组、`a*b` 字符串或其它
 */
function normalizeCondimentAllergen(raw: unknown): string[] {
    if (Array.isArray(raw)) {
        return raw.map((x) => String(x)).filter(Boolean);
    }
    if (typeof raw === 'string') {
        const t = raw.trim();
        if (!t) {
            return [];
        }
        return t.split('*').map((s) => s.trim()).filter(Boolean);
    }
    return [];
}

/**
 * 将本地调味品组映射为云端 create 所需 condiment_group 快照。
 * @param group - get_condiments_group 返回的一组
 */
export function buildCloudDishCondimentGroup(
    group: CloudDishCondimentGroupSource,
): CloudDishLibraryCreateDishCondimentGroup {
    const id = Number(group.scomdiment_groups_id);
    const safeId = Number.isFinite(id) ? id : -1;
    const items = Array.isArray(group.comdiments) ? group.comdiments : [];
    return {
        source_condiment_groups_id: safeId,
        scomdiment_groups_id: safeId,
        comdiment_groups_name: String(group.comdiment_groups_name ?? ''),
        touchscreen_style: group.touchscreen_style ?? null,
        price: Number.isFinite(Number(group.price)) ? Number(group.price) : 0,
        comdiments: items.map((raw) => {
            const c = raw as Record<string, unknown>;
            return {
                item_name1: String(c.item_name1 ?? ''),
                item_name2: String(c.item_name2 ?? ''),
                nlu: c.nlu == null ? null : String(c.nlu),
                tax_group: Number.isFinite(Number(c.tax_group)) ? Number(c.tax_group) : -1,
                major_group: Number.isFinite(Number(c.major_group)) ? Number(c.major_group) : -1,
                family_group: Number.isFinite(Number(c.family_group)) ? Number(c.family_group) : -1,
                price_1: Number.isFinite(Number(c.price_1)) ? Number(c.price_1) : 0,
                allergen: normalizeCondimentAllergen(c.allergen),
                item_description: String(c.item_description ?? ''),
                other_detail_info: String(c.other_detail_info ?? ''),
                takeout_price_1: Number.isFinite(Number(c.takeout_price_1)) ? Number(c.takeout_price_1) : 0,
                unit_1: String(c.unit_1 ?? ''),
            };
        }),
    };
}

/**
 * 按选中调味品组 id 决议是否附带 condiment_group。
 * @param groups - 弹窗缓存的全量组
 * @param requiredCondimentId - 菜品 required_condiment
 * @returns 快照；未选或找不到时 undefined（调用方勿写入 body）
 */
export function resolveCloudDishCondimentGroupForCreate(
    groups: CloudDishCondimentGroupSource[],
    requiredCondimentId: number,
): CloudDishLibraryCreateDishCondimentGroup | undefined {
    if (!Number.isFinite(requiredCondimentId) || requiredCondimentId <= 0) {
        return undefined;
    }
    const found = groups.find((g) => Number(g.scomdiment_groups_id) === requiredCondimentId);
    if (!found) {
        return undefined;
    }
    return buildCloudDishCondimentGroup(found);
}

/**
 * 云端菜品新增 — 请求体。
 * 业务字段：`Required<Pick<MenuDish, …>>`；目标小类为 `cloud_slu_id`（非本地 `dmi_slu_id`）。
 * `unit_1` / `item_description` 强制 string（空串，勿传 null），与 OAS 一致。
 */
export type CloudDishLibraryCreateDishBody = Required<
    Pick<MenuDish, CloudDishLibraryCreateDishMenuDishKeys>
> & {
    /** 目标云端小类 id */
    cloud_slu_id: string;
    /** 规格单位；空时传 `''`（后端校验为 string，勿传 null） */
    unit_1: string;
    /** 备注 */
    item_description: string;
    /** 选中调味品组时的快照；未选时省略本字段 */
    condiment_group?: CloudDishLibraryCreateDishCondimentGroup;
    /**
     * 套餐组快照；套餐（item_type=3）且有配置时提交完整对象数组。
     * 非套餐或不传时由调用方省略或传 `[]`。
     */
    course_groups?: CloudDishLibraryCourseGroup[];
};

/**
 * 云端菜品新增 — 成功响应（联调真实体）
 * 在详情菜品行基础上带回创建回写字段与可选 `condiment_group` 快照。
 * 主路径读顶层 `id`（云端菜品 string id）；`pickCloudDishIdFromCreateResult` 仅兼容异常形态。
 */
export interface CloudDishLibraryCreateDishResult extends CloudDishLibraryMenuDetailDish {
    /** 小类名称（创建回写） */
    dmi_slu_name: string;
    /** 小类下标（创建回写） */
    dmi_slu_index: number;
    /** 创建时间（ISO 8601） */
    created_at: string;
    /** 成本价 1 */
    cost_1?: number | null;
    /** 规格 1 生效起始日；无则 null */
    date_from_1?: string | null;
    /** 来源类型（联调样例 `SW2`） */
    origin_type?: string;
    /** 税率组名称；无则 null */
    tax_group_name?: string | null;
    /** 沽清记录 id */
    bargain_item_id?: number;
    /** 当前沽清剩余数量 */
    bargain_num_cur?: number;
    /** 沽清开始日期 */
    bargain_start_date?: string;
    /** 沽清结束日期 */
    bargain_end_date?: string;
    /** 触屏样式；无则 null */
    touchscreen_style?: unknown | null;
    /** 调味品组快照；未选时可能省略 */
    condiment_group?: CloudDishLibraryCreateDishCondimentGroup | null;
}

/**
 * 云端菜品编辑 — 业务字段（相对 create：无 `cloud_slu_id`；
 * 其余与本地编辑可写字段对齐，含 `required_condiment` /
 * `rules_ignore_the_display` / `rule_lnr_limit_enable` / `not_joining_global_limit`）。
 */
export type CloudDishLibraryUpdateDishFieldKeys =
    | 'item_name1'
    | 'item_name2'
    | 'item_type'
    | 'price_1'
    | 'takeout_price_1'
    | 'unit_1'
    | 'nlu'
    | 'tax_group'
    | 'print_class'
    | 'major_group'
    | 'family_group'
    | 'class_id'
    | 'item_description'
    | 'allergen'
    | 'other_detail_info'
    | 'rule_lnr_max_num'
    | 'rule_table_max_num'
    | 'rule_lnr_limit_price'
    | 'minimum_time'
    | 'special_attributes'
    | 'ext_dish_info'
    | 'item_i18n'
    | 'required_condiment'
    | 'rules_ignore_the_display'
    | 'rule_lnr_limit_enable'
    | 'not_joining_global_limit';

/** 云端菜品编辑 — 请求体业务字段片段 */
export type CloudDishLibraryUpdateDishFields = Required<
    Pick<MenuDish, Exclude<CloudDishLibraryUpdateDishFieldKeys, 'allergen' | 'other_detail_info' | 'unit_1' | 'item_description'>>
> & {
    /** 规格单位；空时传 `''` */
    unit_1: string;
    /** 备注 */
    item_description: string;
    /** 过敏原列表（云端为 string[]，勿传 `a*b`） */
    allergen: string[];
    /** 其它明细 / 配料标签列表 */
    other_detail_info: string[];
};

/**
 * 云端菜品编辑 — 请求体（OAS：`POST .../menus/dishes/update`）。
 * 菜单/菜品 id 均在 body；路径无 `cloud_menu_id`。
 */
export type CloudDishLibraryUpdateDishBody = CloudDishLibraryUpdateDishFields & {
    /** 所属云端菜单 id */
    cloud_menu_id: string;
    /** 待编辑云端菜品 id */
    cloud_dish_id: string;
    /** 成本；缺省 0 */
    cost_1: number;
    /** 限点开关；缺省 0 */
    rule_lnr_enable: number;
    /** 称重必填；缺省 0 */
    weight_entry_required: number;
    /** 套餐组对象数组；无配置时传 `[]` */
    course_groups: CloudDishLibraryCourseGroup[];
    /** 选中调味品组时的快照；未选时省略 */
    condiment_group?: CloudDishLibraryCreateDishCondimentGroup;
};

/** `buildCloudDishLibraryUpdateDishBody` 入参 */
export type BuildCloudDishLibraryUpdateDishBodyInput = {
    /** 云端菜单 id */
    cloudMenuId: string;
    /** 云端菜品 id */
    cloudDishId: string;
    /** 弹窗组装的业务字段 */
    dishFields: CloudDishLibraryUpdateDishFields;
    /** 成本；缺省 0 */
    cost_1?: number;
    /** 限点开关；缺省 0 */
    rule_lnr_enable?: number;
    /** 称重必填；缺省 0 */
    weight_entry_required?: number;
    /** 套餐组对象数组；缺省 `[]` */
    course_groups?: CloudDishLibraryCourseGroup[];
    /** 调味品组快照；未选时省略 */
    condiment_group?: CloudDishLibraryCreateDishCondimentGroup;
};

/**
 * 组装云端菜品编辑请求体（校验菜单/菜品 id 非空）。
 * @param input - 菜单/菜品 id + 业务字段 + 可选扩展
 * @returns OAS 对齐的 update body
 */
export function buildCloudDishLibraryUpdateDishBody(
    input: BuildCloudDishLibraryUpdateDishBodyInput,
): CloudDishLibraryUpdateDishBody {
    const cloudMenuId = String(input.cloudMenuId ?? '').trim();
    const cloudDishId = String(input.cloudDishId ?? '').trim();
    if (!cloudMenuId) {
        throw new Error('cloud_menu_id is required');
    }
    if (!cloudDishId) {
        throw new Error('cloud_dish_id is required');
    }
    const toNum = (v: unknown, fallback: number) =>
        Number.isFinite(Number(v)) ? Number(v) : fallback;
    const body: CloudDishLibraryUpdateDishBody = {
        cloud_menu_id: cloudMenuId,
        cloud_dish_id: cloudDishId,
        ...input.dishFields,
        cost_1: toNum(input.cost_1, 0),
        rule_lnr_enable: toNum(input.rule_lnr_enable, 0),
        weight_entry_required: toNum(input.weight_entry_required, 0),
        course_groups: Array.isArray(input.course_groups) ? input.course_groups : [],
    };
    if (input.condiment_group) {
        body.condiment_group = input.condiment_group;
    }
    return body;
}

/**
 * 将未知值规范为有限数字，否则返回 fallback。
 * @param v - 原始值
 * @param fallback - 缺省
 */
function toFiniteNumber(v: unknown, fallback: number): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/**
 * 将过敏原 / 其它明细规范为 string[]。
 * @param raw - 数组、`a*b` 字符串或其它
 */
function normalizeStringList(raw: unknown): string[] {
    if (Array.isArray(raw)) {
        return raw.map((x) => String(x)).filter(Boolean);
    }
    if (typeof raw === 'string') {
        const t = raw.trim();
        if (!t) {
            return [];
        }
        return t.includes('*')
            ? t.split('*').map((s) => s.trim()).filter(Boolean)
            : [t];
    }
    return [];
}

/**
 * 从选中菜品行组装云端套餐明细（`source_menu_item_id`←`item_id`，`dish_origin_id`←`origin_id`）。
 * @param dish - 选中的菜单菜品（通常来自云端详情）
 * @param overrides - 可覆盖套餐内加价/限点/名称等
 */
export function buildCloudDishCourseDetailFromMenuDish(
    dish: MenuDish,
    overrides?: Partial<Pick<CloudDishLibraryCourseDetail, 'item_course_name' | 'num' | 'price' | 'description' | 'rule_lnr_max_num' | 'unit'>>,
): CloudDishLibraryCourseDetail {
    const itemId = toFiniteNumber(dish.item_id, 0);
    const originId =
        dish.origin_id != null && String(dish.origin_id).trim() !== ''
            ? String(dish.origin_id)
            : String(itemId);
    const name1 = String(dish.item_name1 ?? '');
    return {
        source_menu_item_id: itemId,
        item_course_name: overrides?.item_course_name ?? name1,
        unit: overrides?.unit ?? String(dish.unit_1 ?? ''),
        num: overrides?.num ?? 1,
        price: overrides?.price ?? 0,
        description: overrides?.description ?? '',
        rule_lnr_max_num: overrides?.rule_lnr_max_num ?? 9999,
        dish_origin_id: originId,
        item_name1: name1,
        item_name2: String(dish.item_name2 ?? ''),
        item_type: toFiniteNumber(dish.item_type, 0),
        class_id: toFiniteNumber(dish.class_id, -1),
        tax_group: toFiniteNumber(dish.tax_group, -1),
        major_group: toFiniteNumber(dish.major_group, -1),
        family_group: toFiniteNumber(dish.family_group, -1),
        price_1: toFiniteNumber(dish.price_1, 0),
        allergen: normalizeStringList(dish.allergen),
        item_description: String(dish.item_description ?? ''),
        takeout_price_1: toFiniteNumber(dish.takeout_price_1, 0),
        unit_1: String(dish.unit_1 ?? ''),
        nlu: dish.nlu == null || String(dish.nlu).trim() === '' ? null : String(dish.nlu),
        ext_dish_info: String(dish.ext_dish_info ?? ''),
        other_detail_info: normalizeStringList(dish.other_detail_info),
        not_joining_global_limit: toFiniteNumber(dish.not_joining_global_limit, 0),
        print_class: toFiniteNumber(dish.print_class, -1),
        origin_type: String((dish as { origin_type?: string }).origin_type ?? 'SW2'),
        dish_rule_lnr_max_num: toFiniteNumber(dish.rule_lnr_max_num, 9999),
        rule_lnr_enable: toFiniteNumber(dish.rule_lnr_enable, 0),
        rule_lnr_limit_enable: toFiniteNumber(dish.rule_lnr_limit_enable, 0),
        rule_lnr_limit_price: toFiniteNumber(dish.rule_lnr_limit_price, 0),
        rule_lnr_max_num_with_customer: toFiniteNumber(dish.rule_lnr_max_num_with_customer, 0),
        rule_table_max_num: toFiniteNumber(dish.rule_table_max_num, 9999),
    };
}

/**
 * UI 套餐草稿明细（兼容本地 `CourseDetail` + 云端快照字段）。
 * 云端草稿在内存中携带完整 `CloudDishLibraryCourseDetail`，便于保存时原样提交。
 */
export type CloudCourseDetailUiDraft = CourseDetail &
    Partial<CloudDishLibraryCourseDetail> & {
        /** 若已完整映射则优先用此快照提交 */
        cloud_snapshot?: CloudDishLibraryCourseDetail;
    };

/** UI 套餐组草稿（本地 CourseGroup + 云端明细草稿） */
export type CloudCourseGroupUiDraft = Omit<CourseGroup, 'course_detail'> & {
    /** 组内明细草稿 */
    course_detail?: CloudCourseDetailUiDraft[];
};

/**
 * 将单条 UI 明细草稿规范为云端 API `course_detail` 元素。
 * @param row - UI 草稿行
 */
export function normalizeCloudCourseDetailUiDraft(row: CloudCourseDetailUiDraft): CloudDishLibraryCourseDetail {
    if (row.cloud_snapshot) {
        return {
            ...row.cloud_snapshot,
            item_course_name: String(row.item_course_name ?? row.cloud_snapshot.item_course_name ?? ''),
            unit: String(row.unit ?? row.cloud_snapshot.unit ?? ''),
            num: toFiniteNumber(row.num, row.cloud_snapshot.num),
            price: toFiniteNumber(row.price, row.cloud_snapshot.price),
            description: String(row.description ?? row.cloud_snapshot.description ?? ''),
            rule_lnr_max_num: toFiniteNumber(row.rule_lnr_max_num, row.cloud_snapshot.rule_lnr_max_num),
        };
    }
    const itemId = toFiniteNumber(row.source_menu_item_id ?? row.menu_item_id, 0);
    return {
        source_menu_item_id: itemId,
        item_course_name: String(row.item_course_name ?? row.item_name1 ?? ''),
        unit: String(row.unit ?? row.unit_1 ?? ''),
        num: toFiniteNumber(row.num, 1),
        price: toFiniteNumber(row.price, 0),
        description: String(row.description ?? ''),
        rule_lnr_max_num: toFiniteNumber(row.rule_lnr_max_num, 9999),
        dish_origin_id: String(row.dish_origin_id ?? itemId),
        item_name1: String(row.item_name1 ?? row.item_course_name ?? ''),
        item_name2: String(row.item_name2 ?? ''),
        item_type: toFiniteNumber(row.item_type, 0),
        class_id: toFiniteNumber(row.class_id, -1),
        tax_group: toFiniteNumber(row.tax_group, -1),
        major_group: toFiniteNumber(row.major_group, -1),
        family_group: toFiniteNumber(row.family_group, -1),
        price_1: toFiniteNumber(row.price_1, 0),
        allergen: normalizeStringList(row.allergen),
        item_description: String(row.item_description ?? ''),
        takeout_price_1: toFiniteNumber(row.takeout_price_1, 0),
        unit_1: String(row.unit_1 ?? row.unit ?? ''),
        nlu: row.nlu == null || String(row.nlu).trim() === '' ? null : String(row.nlu),
        ext_dish_info: String(row.ext_dish_info ?? ''),
        other_detail_info: normalizeStringList(row.other_detail_info),
        not_joining_global_limit: toFiniteNumber(row.not_joining_global_limit, 0),
        print_class: toFiniteNumber(row.print_class, -1),
        origin_type: String(row.origin_type ?? 'SW2'),
        dish_rule_lnr_max_num: toFiniteNumber(row.dish_rule_lnr_max_num ?? row.rule_lnr_max_num, 9999),
        rule_lnr_enable: toFiniteNumber(row.rule_lnr_enable, 0),
        rule_lnr_limit_enable: toFiniteNumber(row.rule_lnr_limit_enable, 0),
        rule_lnr_limit_price: toFiniteNumber(row.rule_lnr_limit_price, 0),
        rule_lnr_max_num_with_customer: toFiniteNumber(row.rule_lnr_max_num_with_customer, 0),
        rule_table_max_num: toFiniteNumber(row.rule_table_max_num, 9999),
    };
}

/**
 * 将 UI 套餐组草稿序列化为云端 `course_groups` 请求体。
 * @param groups - 弹窗内草稿
 */
export function buildCloudDishCourseGroupsFromUi(
    groups: CloudCourseGroupUiDraft[],
): CloudDishLibraryCourseGroup[] {
    return (groups || []).map((g, index) => ({
        course_group_name: String(g.course_group_name ?? ''),
        is_must: toFiniteNumber(g.is_must, 0),
        choose_num: toFiniteNumber(g.choose_num, 1),
        slu_id: toFiniteNumber(g.slu_id, -1),
        origin_type: String((g as { origin_type?: string }).origin_type ?? 'SW2'),
        origin_id: String((g as { origin_id?: string }).origin_id ?? ''),
        origin_dish_type: String((g as { origin_dish_type?: string }).origin_dish_type ?? ''),
        slu_priority: toFiniteNumber((g as { slu_priority?: number }).slu_priority, index),
        is_full_select: toFiniteNumber(g.is_full_select, 0),
        course_detail: (g.course_detail || []).map((row) => normalizeCloudCourseDetailUiDraft(row)),
    }));
}

/**
 * 将详情回填的 `course_groups` 转为弹窗 UI 草稿（带临时 detail_id / course_group_id）。
 * @param groups - 详情菜品行上的套餐组
 */
export function cloudCourseGroupsToUiDraft(
    groups: CloudDishLibraryCourseGroup[] | null | undefined,
): CloudCourseGroupUiDraft[] {
    if (!Array.isArray(groups) || groups.length === 0) {
        return [];
    }
    return groups.map((g, gi) => ({
        course_group_id: gi + 1,
        course_group_name: g.course_group_name,
        is_must: g.is_must,
        choose_num: g.choose_num,
        slu_id: g.slu_id,
        is_full_select: g.is_full_select,
        course_detail: (g.course_detail || []).map((d, di) => ({
            detail_id: di + 1,
            menu_item_id: d.source_menu_item_id,
            item_name1: d.item_name1,
            item_course_name: d.item_course_name,
            unit: d.unit,
            num: d.num,
            price: d.price,
            description: d.description,
            item_description: d.item_description,
            rule_lnr_max_num: d.rule_lnr_max_num,
            cloud_snapshot: d,
        })),
    }));
}

/**
 * 云端菜单列表 → 选菜弹窗骨架 `FirstMenu[]`（小类无菜品，点击后再 merge 详情）。
 * `main_group_id` 用列表下标+1；云端菜单 string id 写在 `id` 上供懒加载。
 * @param list - `GET .../menus` 列表
 */
export function mapCloudMenuListToPickerSkeleton(
    list: CloudDishLibraryMenuListItem[],
): FirstMenu[] {
    return (list || []).map((m, index) => {
        const slus = Array.isArray(m.menu_item_slu) ? m.menu_item_slu : [];
        return {
            main_group_id: index + 1,
            main_group_name: m.main_group_name,
            id: m.id,
            menu_item_slu: slus.map((s) => ({
                dmi_slu_id: Number(s.dmi_slu_id) || 0,
                dmi_slu_name: s.dmi_slu_name,
                dmi_slu_index: s.dmi_slu_index,
                not_joining_global_limit: s.not_joining_global_limit,
                category_order_limit: s.category_order_limit,
                table_category_order_limit: s.table_category_order_limit,
                dmi_slu_name_i18n: s.dmi_slu_name_i18n,
                _id: s._id,
                menu_item: [],
            })),
        } as FirstMenu;
    });
}

/**
 * 将云端菜单详情菜品树 merge 进选菜骨架中对应菜单（按 `id` 匹配）。
 * @param skeleton - 当前选菜树
 * @param detail - `GET .../menus/{id}` 详情
 */
export function mergeCloudMenuDetailIntoPickerSkeleton(
    skeleton: FirstMenu[],
    detail: CloudDishLibraryMenuDetail,
): FirstMenu[] {
    const cloudId = String(detail?.id ?? '').trim();
    if (!cloudId) {
        return skeleton;
    }
    return skeleton.map((fm) => {
        const fmId = String((fm as { id?: string }).id ?? '').trim();
        if (fmId !== cloudId) {
            return fm;
        }
        const detailSlus = detail.menu_item_slu || [];
        return {
            ...fm,
            main_group_name: detail.main_group_name ?? fm.main_group_name,
            menu_item_slu: detailSlus.map((s) => ({
                ...s,
                menu_item: Array.isArray(s.menu_item) ? (s.menu_item as unknown as MenuDish[]) : [],
            })),
        } as unknown as FirstMenu;
    });
}

/**
 * 云端菜品编辑 — 成功响应（OAS schema 为空；联调后可收紧）。
 * 暂与创建结果同形，便于刷新前读回 `id`。
 */
export type CloudDishLibraryUpdateDishResult = CloudDishLibraryCreateDishResult;

/**
 * 云端菜品批量设置 — 可选补丁字段（相对单菜 update：可含 `required_condiment`；
 * 不含本地批量里的 `rules_ignore_the_display` / `rule_lnr_limit_enable`）。
 * 顶栏「不受轮次限制」走独立 `batch-not-limit`，不在此补丁内。
 */
export type CloudDishLibraryBatchUpdateDishPatch = Partial<{
    /** 名称1 */
    item_name1: string;
    /** 名称2 */
    item_name2: string;
    /** 菜品类型 */
    item_type: number;
    /** 堂食价 */
    price_1: number;
    /** 外卖价 */
    takeout_price_1: number;
    /** 规格单位 */
    unit_1: string;
    /** 成本 */
    cost_1: number;
    /** 助记码 */
    nlu: string;
    /** 税率组 */
    tax_group: number;
    /** 岗位打印组 */
    print_class: number;
    /** 自定义报表主类别 */
    major_group: number;
    /** 自定义报表子类别 */
    family_group: number;
    /** 可选调味品组 id（未选可为 -1） */
    required_condiment: number;
    /** 折扣分项等 class */
    class_id: number;
    /** 备注 */
    item_description: string;
    /** 过敏原列表 */
    allergen: string[];
    /** 其它明细 / 配料标签 */
    other_detail_info: string[];
    /** 限点开关 */
    rule_lnr_enable: number;
    /** 每人限点 */
    rule_lnr_max_num: number;
    /** 桌台限点 */
    rule_table_max_num: number;
    /** 称重必填 */
    weight_entry_required: number;
    /** 多语言 JSON */
    item_i18n: string;
    /** 调味品组快照 */
    condiment_group: CloudDishLibraryCreateDishCondimentGroup;
    /** 计时最短时长 */
    minimum_time: number;
    /** 额外备注 */
    ext_dish_info: string;
    /** 特殊属性 */
    special_attributes: number;
    /** 超限后价格 */
    rule_lnr_limit_price: number;
}>;

/**
 * 云端菜品批量设置 — 请求体（OAS：`POST .../menus/dishes/batch-update`）。
 * 前端只提交已改字段 + 必填 id（OAS 全量 required 按过标处理）。
 */
export type CloudDishLibraryBatchUpdateDishBody = CloudDishLibraryBatchUpdateDishPatch & {
    /** 所属云端菜单 id */
    cloud_menu_id: string;
    /** 待批量修改的云端菜品 id 列表 */
    cloud_dish_ids: string[];
};

/** `buildCloudDishLibraryBatchUpdateDishBody` 入参 */
export type BuildCloudDishLibraryBatchUpdateDishBodyInput = {
    /** 云端菜单 id */
    cloudMenuId: string;
    /** 云端菜品 id 列表 */
    cloudDishIds: string[];
    /** 已改业务字段（至少一项） */
    patch: CloudDishLibraryBatchUpdateDishPatch;
};

/**
 * 组装云端菜品批量设置请求体。
 * @param input - 菜单 id + 菜品 id 列表 + 局部补丁
 */
export function buildCloudDishLibraryBatchUpdateDishBody(
    input: BuildCloudDishLibraryBatchUpdateDishBodyInput,
): CloudDishLibraryBatchUpdateDishBody {
    const cloudMenuId = String(input.cloudMenuId ?? '').trim();
    if (!cloudMenuId) {
        throw new Error('cloud_menu_id is required');
    }
    const cloudDishIds = (Array.isArray(input.cloudDishIds) ? input.cloudDishIds : [])
        .map((id) => String(id ?? '').trim())
        .filter(Boolean);
    if (cloudDishIds.length === 0) {
        throw new Error('cloud_dish_ids is required');
    }
    const patch = input.patch ?? {};
    const patchKeys = Object.keys(patch).filter((k) => (patch as Record<string, unknown>)[k] !== undefined);
    if (patchKeys.length === 0) {
        throw new Error('patch is required');
    }
    return {
        cloud_menu_id: cloudMenuId,
        cloud_dish_ids: cloudDishIds,
        ...patch,
    };
}

/** 云端菜品批量设置 — 成功响应（OAS schema 为空） */
export type CloudDishLibraryBatchUpdateDishResult = unknown;

/**
 * 云端菜单批量「不受轮次限制」— 请求体（OAS：`POST .../menus/{cloud_menu_id}/batch-not-limit`）。
 */
export interface CloudDishLibraryBatchNotLimitBody {
    /** 不受轮次限制：1=不受限，0=受限制 */
    not_joining_global_limit: number;
}

/**
 * 云端菜单批量「不受轮次限制」— 成功响应。
 */
export interface CloudDishLibraryBatchNotLimitResult {
    /** 云端菜单 id */
    cloud_menu_id: string;
    /** 写入后的轮次限制值：1=不受限，0=受限制 */
    not_joining_global_limit: number;
    /** 本次更新的菜品数 */
    updated_dish_count: number;
}

/**
 * 组装云端批量不受轮次限制请求体。
 * @param notJoiningGlobalLimit - `0` 或 `1`
 */
export function buildCloudDishLibraryBatchNotLimitBody(
    notJoiningGlobalLimit: number,
): CloudDishLibraryBatchNotLimitBody {
    const value = Number(notJoiningGlobalLimit);
    if (value !== 0 && value !== 1) {
        throw new Error('not_joining_global_limit must be 0 or 1');
    }
    return { not_joining_global_limit: value };
}

/** 删除云端菜单图或菜品图 — 请求体 */
export interface CloudDishLibraryImagesDeleteBody {
    /** `'menu'` 删菜单图；`'dish'` 删菜品图 */
    type: 'menu' | 'dish';
    /** 云端菜单 id */
    cloud_menu_id: string;
    /** 云端菜品 id（`type === 'dish'` 时必填） */
    cloud_dish_id?: string;
}

/**
 * 删除云端菜单图或菜品图 — 成功响应（联调真实体，`type: menu`）
 * 例：`{ type, id, menu_image, hasImage, deleted_oss, updated_at }`
 */
export interface CloudDishLibraryImagesDeleteResult {
    /** `'menu'` 删菜单图；`'dish'` 删菜品图 */
    type: 'menu' | 'dish';
    /** 被删图所属资源 id（菜单或菜品） */
    id: string;
    /** 删后封面 URL；删除成功后通常为 `''` */
    menu_image: string;
    /** 是否仍有图 */
    hasImage: boolean;
    /** 是否已从 OSS 删除 */
    deleted_oss: boolean;
    /** 最近更新时间（ISO 8601） */
    updated_at: string;
}

/**
 * Pixabay 搜图 — 查询参数
 * `GET /v3/cloud-dish-library/{string_id}/images/pixabay-search`
 */
export interface CloudDishLibraryPixabaySearchParams {
    /** 搜索关键词（必填） */
    q: string;
    /** Pixabay 语言码（必填，如 `zh` / `en` / `it` / `es`） */
    lang: string;
    /** 图片类型，如 `all` / `photo` / `illustration` / `vector` */
    image_type?: string;
    /** 方向，如 `all` / `horizontal` / `vertical` */
    orientation?: string;
    /** 分类（Pixabay category） */
    category?: string;
    /** 安全搜索，如 `true` / `false` */
    safesearch?: string | boolean;
    /** 排序，如 `popular` / `latest` */
    order?: string;
    /** 页码，从 1 起 */
    page?: number;
    /** 每页条数 */
    per_page?: number;
}

/** Pixabay 单条命中（网格用 `previewURL`，进裁切优先 `largeImageURL`） */
export interface CloudDishLibraryPixabayHit {
    /** Pixabay 图片 id */
    id: number;
    /** 标签字符串 */
    tags: string;
    /** 网格缩略图 URL */
    previewURL: string;
    /** 缩略图宽（px） */
    previewWidth?: number;
    /** 缩略图高（px） */
    previewHeight?: number;
    /** 中图 URL（约 640） */
    webformatURL: string;
    /** 中图宽（px） */
    webformatWidth?: number;
    /** 中图高（px） */
    webformatHeight?: number;
    /** 大图 URL（约 1280，选中后优先） */
    largeImageURL: string;
    /** Pixabay 详情页 */
    pageURL?: string;
    /** 作者昵称 */
    user?: string;
    /** 作者 id */
    user_id?: number;
}

/**
 * Pixabay 搜图 — 成功响应
 * 分页建议：`Math.ceil(totalHits / per_page)`
 */
export interface CloudDishLibraryPixabaySearchResult {
    /** 提供方标识，如 `pixabay` */
    provider: string;
    /** 署名文案 */
    attribution: string;
    /** 总匹配数（可能大于可返回上限） */
    total: number;
    /** 可返回命中上限（如 500） */
    totalHits: number;
    /** 当前页 */
    page: number;
    /** 每页条数 */
    per_page: number;
    /** 命中列表 */
    hits: CloudDishLibraryPixabayHit[];
}

/** 按 URL 拉取图片 — 请求体 */
export interface CloudDishLibraryFetchFromUrlBody {
    /** 图片网络地址 */
    url: string;
}

/**
 * 云端菜单导入到所选门店 — 请求体
 * `POST /v3/cloud-dish-library/{string_id}/menus/import-to-local`
 * 对照本地 `POST /v3/menu/share`：`cloud_menu_id` ↔ `main_group_ids`，`string_ids` ↔ `to_restaurant_string_ids`
 */
export interface CloudDishLibraryImportToLocalBody {
    /** 源云端菜单 id */
    cloud_menu_id: string;
    /** 目标门店餐厅码列表 */
    string_ids: string[];
    /** 集团码；来源与目标须同属该集团 */
    group_string_id: string;
}

/**
 * 导入成功后单店结果（OAS 成功示例）
 */
export interface CloudDishLibraryImportToLocalStoreResult {
    /** 目标门店餐厅码 */
    string_id: string;
    /** 该店新建的本地一级菜单 id */
    main_group_id: number;
    /** 新建本地菜单名称 */
    main_group_name: string;
    /** 二级菜单（小类）数 */
    second_group_count: number;
    /** 菜品数 */
    dish_count: number;
}

/**
 * 云端菜单导入到所选门店 — 成功响应（OAS 成功示例；schema 为空）
 * 例：`{ ok, count, results }`
 */
export interface CloudDishLibraryImportToLocalResult {
    /** 是否整体成功 */
    ok: boolean;
    /** 成功写入的门店数（通常等于 `results.length`） */
    count: number;
    /** 各目标门店新建本地菜单摘要 */
    results: CloudDishLibraryImportToLocalStoreResult[];
}

/**
 * 从菜单更新响应中取首个新增小类 `_id`（主路径：`added_slu[0]._id`）。
 * @param res - `menus/{id}/update` 响应
 * @returns 云端小类 id；无有效 `_id` 时为 null
 */
export function pickFirstAddedCloudSluId(
    res: CloudDishLibraryUpdateMenuResult | null | undefined,
): string | null {
    const id = res?.added_slu?.[0]?._id;
    if (typeof id === 'string' && id.trim()) {
        return id.trim();
    }
    return null;
}

/**
 * 从创建云端菜单响应中解析新建的 `cloud_menu_id`。
 * 首选直接读 `CloudDishLibraryCreateMenuResult.id`；本函数保留 `_id` / `cloud_menu_id` / `menu.id` 等兜底。
 * @param res - 创建接口响应
 * @returns 云端菜单 id；无法解析时为 null
 */
export function pickCloudMenuIdFromCreateResult(res: unknown): string | null {
    if (res == null || typeof res !== 'object') {
        return null;
    }
    const o = res as Record<string, unknown>;
    const direct = o.id ?? o._id ?? o.cloud_menu_id;
    if (typeof direct === 'string' && direct.trim()) {
        return direct.trim();
    }
    if (typeof direct === 'number' && Number.isFinite(direct)) {
        return String(direct);
    }
    const nested = o.menu;
    if (nested && typeof nested === 'object') {
        const m = nested as Record<string, unknown>;
        const mid = m.id ?? m._id;
        if (typeof mid === 'string' && mid.trim()) {
            return mid.trim();
        }
    }
    return null;
}

/**
 * 从新增云端菜品响应中解析 `cloud_dish_id`。
 * @param res - 菜品创建响应
 * @returns 云端菜品 id；无法解析时为 null
 */
export function pickCloudDishIdFromCreateResult(res: unknown): string | null {
    if (res == null || typeof res !== 'object') {
        return null;
    }
    const o = res as Record<string, unknown>;
    const direct = o.id ?? o._id ?? o.cloud_dish_id;
    if (typeof direct === 'string' && direct.trim()) {
        return direct.trim();
    }
    return null;
}

/**
 * 从上传图响应中解析可回填的文件名/URL（兼容 restaurant-static 与云端空 schema）。
 * @param res - 上传响应
 * @returns 展示用字符串；无法解析时为 null
 */
export function pickCloudImageUploadRef(res: unknown): string | null {
    if (res == null) {
        return null;
    }
    if (typeof res === 'string' && res.trim()) {
        return res.trim();
    }
    if (typeof res !== 'object') {
        return null;
    }
    const o = res as Record<string, unknown>;
    const candidates = [o.file_name, o.filename, o.url, o.menu_image, o.dish_image, o.path, o.id];
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) {
            return c.trim();
        }
    }
    return null;
}

/**
 * 云端菜品库 API（`/v3/cloud-dish-library/{string_id}/...`）。
 * 与旧扁平 `menu_library` 无关；经 `apiManage_rbp` 暴露。
 */
const cloudDishLibraryApi = {
    /**
     * 上传本地菜单到云端（只新增）。
     * @param body - `main_group_ids`
     * @param rbpStringId - 可选餐厅码（菜单库模块内门店）
     * @returns 上传统计：`uploaded_count` / `total_dish_count` / `menus`
     */
    async post_cloud_dish_library_menus_upload(
        body: CloudDishLibraryUploadBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryUploadResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/menus/upload`, body);
    },

    /**
     * 获取云端菜单列表（含小类摘要 `menu_item_slu`，无菜品树）。
     * @param rbpStringId - 可选餐厅码
     * @returns 云端菜单摘要数组（联调真实体为顶层数组；元素可含 `menu_item_slu`）
     */
    async get_cloud_dish_library_menus(
        rbpStringId?: string,
    ): Promise<CloudDishLibraryMenuListItem[]> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.get(`${BASE_URL_3}/cloud-dish-library/${sid}/menus`);
    },

    /**
     * 云端菜单详情（小类 + 菜品；结构为本地 `FirstMenu` 扩展）。
     * @param cloudMenuId - 云端菜单 id
     * @param rbpStringId - 可选餐厅码
     * @returns 云端详情体（含 `id` / `source_main_group_id` / `menu_item_slu`）
     */
    async get_cloud_dish_library_menu_detail(
        cloudMenuId: string,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryMenuDetail> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.get(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}`,
        );
    },

    /**
     * 删除云端菜单（可批量）。
     * @param body - `cloud_menu_ids`
     * @param rbpStringId - 餐厅码（必填语义，由 resolve 校验）
     * @returns 删除统计：`deleted_menu_count` / `deleted_dish_count` / `deleted_oss_count` / `not_found_ids`
     */
    async post_cloud_dish_library_menus_delete(
        body: CloudDishLibraryDeleteBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryDeleteResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/menus/delete`, body);
    },

    /**
     * 删除单个云端菜单。
     * @param cloudMenuId - 待删除的云端菜单 id
     * @param rbpStringId - 餐厅码
     * @returns 删除统计
     */
    async post_cloud_dish_library_menu_delete(
        cloudMenuId: string,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryDeleteResult> {
        return await this.post_cloud_dish_library_menus_delete(
            buildCloudDishLibrarySingleMenuDeleteBody(cloudMenuId),
            rbpStringId,
        );
    },

    /**
     * 将选中云端菜品添加到本地菜单/小类。
     * @param body - 云端菜单/菜品与本地目标 id
     * @param rbpStringId - 可选餐厅码
     * @returns 添加统计：`added_count` / `skipped_duplicate_count` / 名称与套餐·配料附带创建信息
     */
    async post_cloud_dish_library_add_dishes(
        body: CloudDishLibraryAddDishesBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryAddDishesResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/menus/add-dishes`, body);
    },

    /**
     * 新增云端菜单（头区；本前端 `menu_item_slu` 先传空，小类再走 update 追加）。
     * @param body - 创建体
     * @param rbpStringId - 餐厅码
     * @returns 新建云端菜单摘要（含 `id` / `create_source` / `updated_at` 等）
     */
    async post_cloud_dish_library_menus_create(
        body: CloudDishLibraryCreateMenuBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryCreateMenuResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/menus/create`, body);
    },

    /**
     * 编辑云端菜单（头区与/或小类增改删；支持局部字段）。
     * @param cloudMenuId - 云端菜单 id
     * @param body - 更新体
     * @param rbpStringId - 餐厅码
     */
    async post_cloud_dish_library_menu_update(
        cloudMenuId: string,
        body: CloudDishLibraryUpdateMenuBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryUpdateMenuResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}/update`,
            body,
        );
    },

    /**
     * 删除云端菜单中的单个空小类。
     * @param cloudMenuId - 云端菜单 id
     * @param cloudSluId - 云端小类 id
     * @param rbpStringId - 餐厅码
     * @returns 更新后的菜单结果
     */
    async post_cloud_dish_library_subcategory_delete(
        cloudMenuId: string,
        cloudSluId: string,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryUpdateMenuResult> {
        return await this.post_cloud_dish_library_menu_update(
            cloudMenuId,
            buildCloudDishLibrarySubcategoryDeleteBody(cloudSluId),
            rbpStringId,
        );
    },

    /**
     * 在指定云端菜单的小类内新增菜品。
     * @param cloudMenuId - 云端菜单 id
     * @param body - 菜品体（含 `cloud_slu_id`）
     * @param rbpStringId - 餐厅码
     * @returns 新建云端菜品（联调真实体；主路径读 `id`）
     */
    async post_cloud_dish_library_dishes_create(
        cloudMenuId: string,
        body: CloudDishLibraryCreateDishBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryCreateDishResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}/dishes/create`,
            body,
        );
    },

    /**
     * 编辑云端菜品（路径无 cloud_menu_id；菜单/菜品 id 在 body）。
     * @param body - `cloud_menu_id` + `cloud_dish_id` + 菜品字段
     * @param rbpStringId - 餐厅码
     * @returns 编辑结果（OAS schema 为空；类型暂同 create）
     */
    async post_cloud_dish_library_dishes_update(
        body: CloudDishLibraryUpdateDishBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryUpdateDishResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/menus/dishes/update`, body);
    },

    /**
     * 批量修改云端菜品（局部字段 + `cloud_dish_ids`）。
     * @param body - `cloud_menu_id` + `cloud_dish_ids` + 已改字段
     * @param rbpStringId - 餐厅码
     */
    async post_cloud_dish_library_dishes_batch_update(
        body: CloudDishLibraryBatchUpdateDishBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryBatchUpdateDishResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/menus/dishes/batch-update`, body);
    },

    /**
     * 批量设置当前云端菜单下全部菜品「不受轮次限制」。
     * @param cloudMenuId - 云端菜单 id（path）
     * @param body - `{ not_joining_global_limit: 0|1 }`
     * @param rbpStringId - 餐厅码
     */
    async post_cloud_dish_library_menus_batch_not_limit(
        cloudMenuId: string,
        body: CloudDishLibraryBatchNotLimitBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryBatchNotLimitResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        const mid = String(cloudMenuId ?? '').trim();
        if (!mid) {
            throw new Error('cloud_menu_id is required');
        }
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(mid)}/batch-not-limit`,
            body,
        );
    },

    /**
     * 上传/覆盖云端菜单图（FormData 空字段名，与本地 restaurant-static 一致）。
     * @param cloudMenuId - 云端菜单 id
     * @param imageFile - 图片文件
     * @param rbpStringId - 餐厅码
     * @returns 含 `id` / `menu_image` / `hasImage` / `updated_at`（主路径读 `menu_image`）
     */
    async post_cloud_dish_library_menu_image(
        cloudMenuId: string,
        imageFile: File,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryMenuImageResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        const formData = new FormData();
        formData.append('', imageFile);
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}/image`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
    },

    /**
     * 上传云端菜品图（FormData 空字段名，与本地一致）。
     * @param cloudMenuId - 云端菜单 id
     * @param cloudDishId - 云端菜品 id
     * @param imageFile - 图片文件
     * @param rbpStringId - 餐厅码
     */
    async post_cloud_dish_library_dish_image(
        cloudMenuId: string,
        cloudDishId: string,
        imageFile: File,
        rbpStringId?: string,
    ): Promise<unknown> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        const formData = new FormData();
        formData.append('', imageFile);
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}/dishes/${encodeURIComponent(cloudDishId)}/image`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
    },

    /**
     * 批量删除云端菜品。
     * @param cloudMenuId - 源云端菜单 id（路径参数）
     * @param body - `cloud_dish_ids`
     * @param rbpStringId - 餐厅码
     * @returns 删除统计：`deleted_dish_count` / `deleted_oss_count` / `not_found_ids` / `dish_count`
     */
    async post_cloud_dish_library_dishes_delete(
        cloudMenuId: string,
        body: CloudDishLibraryDishesDeleteBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryDishesDeleteResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}/dishes/delete`,
            body,
        );
    },

    /**
     * 转移云端菜品到目标菜单/小类（可跨云端菜单）。
     * @param cloudMenuId - 源云端菜单 id（路径参数）
     * @param body - `cloud_dish_ids` + `target_cloud_menu_id` / `target_cloud_slu_id`
     * @param rbpStringId - 餐厅码
     * @returns 转移统计：`moved_count` / `moved_ids` / `skipped_same_target_count` / `not_found_ids` 等
     */
    async post_cloud_dish_library_dishes_move(
        cloudMenuId: string,
        body: CloudDishLibraryDishesMoveBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryDishesMoveResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}/dishes/move`,
            body,
        );
    },

    /**
     * 复制云端菜品到目标菜单/小类（可跨云端菜单）。
     * @param cloudMenuId - 源云端菜单 id（路径参数）
     * @param body - `cloud_dish_ids` + `target_cloud_menu_id` + `target_cloud_slu_id`（均必填）
     * @param rbpStringId - 餐厅码
     * @returns 复制统计：`copied_count` / `copied_dishes` / `not_found_ids` / `target_dish_count`
     */
    async post_cloud_dish_library_dishes_copy(
        cloudMenuId: string,
        body: CloudDishLibraryDishesCopyBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryDishesCopyResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(cloudMenuId)}/dishes/copy`,
            body,
        );
    },

    /**
     * 修改云端菜单某小类下的菜品排序。
     * @param cloudMenuId - 云端菜单 id（路径参数）
     * @param body - `cloud_slu_id` + `dishes[{ cloud_dish_id, slu_priority }]`
     * @param rbpStringId - 餐厅码
     */
    async post_cloud_dish_library_dishes_sort(
        cloudMenuId: string,
        body: CloudDishLibraryDishesSortBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryDishesSortResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        const mid = String(cloudMenuId ?? '').trim();
        if (!mid) {
            throw new Error('cloud_menu_id is required');
        }
        return await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/menus/${encodeURIComponent(mid)}/dishes/sort`,
            body,
        );
    },

    /**
     * 删除云端菜单图或菜品图。
     * @param body - `type` + `cloud_menu_id`（删菜品图另带 `cloud_dish_id`）
     * @param rbpStringId - 餐厅码
     * @returns 含 `type` / `id` / `menu_image` / `hasImage` / `deleted_oss` / `updated_at`
     */
    async post_cloud_dish_library_images_delete(
        body: CloudDishLibraryImagesDeleteBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryImagesDeleteResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/images/delete`, body);
    },

    /**
     * Pixabay 关键词搜图（网格预览用 `previewURL`，选中后应用 `largeImageURL` 再 fetch）。
     * @param params - 查询参数（`q` / `lang` 必填）
     * @param rbpStringId - 餐厅码
     */
    async get_cloud_dish_library_images_pixabay_search(
        params: CloudDishLibraryPixabaySearchParams,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryPixabaySearchResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        const q = String(params?.q ?? '').trim();
        const lang = String(params?.lang ?? '').trim();
        if (!q) {
            throw new Error('pixabay-search: q is required');
        }
        if (!lang) {
            throw new Error('pixabay-search: lang is required');
        }
        return await http.get(`${BASE_URL_3}/cloud-dish-library/${sid}/images/pixabay-search`, {
            params: {
                q,
                lang,
                ...(params.image_type != null ? { image_type: params.image_type } : {}),
                ...(params.orientation != null ? { orientation: params.orientation } : {}),
                ...(params.category != null ? { category: params.category } : {}),
                ...(params.safesearch != null ? { safesearch: params.safesearch } : {}),
                ...(params.order != null ? { order: params.order } : {}),
                ...(params.page != null ? { page: params.page } : {}),
                ...(params.per_page != null ? { per_page: params.per_page } : {}),
            },
        });
    },

    /**
     * 按 URL 拉取图片二进制（JPEG Blob）。须 `responseType: 'blob'`，禁止当 JSON 解析。
     * @param body - `{ url }` 图片地址
     * @param rbpStringId - 餐厅码
     * @returns 图片 Blob；若服务端以 JSON 报错则抛错
     */
    async post_cloud_dish_library_images_fetch_from_url(
        body: CloudDishLibraryFetchFromUrlBody,
        rbpStringId?: string,
    ): Promise<Blob> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        const url = String(body?.url ?? '').trim();
        if (!url) {
            throw new Error('fetch-from-url: url is required');
        }
        const res = await http.post(
            `${BASE_URL_3}/cloud-dish-library/${sid}/images/fetch-from-url`,
            { url },
            { responseType: 'blob' },
        );
        // 拦截器失败态：`{ error: true, ... }`（非 Blob）
        if (res && typeof res === 'object' && !(res instanceof Blob) && (res as { error?: boolean }).error) {
            throw new Error('fetch-from-url failed');
        }
        if (!(res instanceof Blob)) {
            throw new Error('fetch-from-url: expected Blob response');
        }
        // 业务错误偶发以 JSON Blob 返回
        const mime = (res.type || '').toLowerCase();
        if (mime.includes('application/json') || mime.includes('text/')) {
            const text = await res.text();
            let message = 'fetch-from-url failed';
            try {
                const parsed = JSON.parse(text) as { message?: string; error?: { message?: string } };
                message = parsed?.error?.message || parsed?.message || message;
            } catch {
                // ignore parse
            }
            throw new Error(message);
        }
        return res;
    },

    /**
     * 将云端菜单新增到所选门店本地菜单（集团内多店）。
     * 对照本地 `POST /v3/menu/share`；path `string_id` 为菜单库当前源餐厅。
     * @param body - `cloud_menu_id` + `string_ids` + `group_string_id`
     * @param rbpStringId - 源餐厅码（path）
     * @returns `{ ok, count, results }`（OAS 成功示例）
     */
    async post_cloud_dish_library_menus_import_to_local(
        body: CloudDishLibraryImportToLocalBody,
        rbpStringId?: string,
    ): Promise<CloudDishLibraryImportToLocalResult> {
        const sid = resolveCloudDishLibraryStringId(rbpStringId);
        return await http.post(`${BASE_URL_3}/cloud-dish-library/${sid}/menus/import-to-local`, body);
    },
};

export default cloudDishLibraryApi;
